// Field definitions for the enquiry details drawer.
//
// The borrower question set was reworked, which splits the fields here into
// three groups:
//
//   * New questions, whose answers live in new columns or in one of the four
//     new child tables (employment_details, business_details,
//     other_income_sources, balance_transfer_details).
//   * Questions no longer asked, dropped from this file. The columns are
//     retained in the database for the enquiries that already hold them, but
//     nothing writes them now, so showing them would be misleading.
//   * Questions replaced by a reworked equivalent. The answer lives in the new
//     location for enquiries created since the rework and in the old one for
//     everything before it, so these declare `source` and `fallback`: the new
//     path is read first and the old one only when it has no value.
//
// `source` and `fallback` are dotted paths from the ROOT of the payload, not
// from the section a field is declared under, because a replacement often
// moved the answer into a different section entirely - the salaried income
// figure moved from income_assessment to employment_details, for instance.

export const drawerSchema = {
    include: true,
    fields: {
        loan_type: {
            include: true,
            type: "nested",
            label: "Loan Type",
            fields: {
                id: { include: false, type: "text", label: "ID" },
                code: { include: false, type: "text", label: "Code" },
                name: { include: true, type: "text", label: "Name" },
                created_at: { include: false, type: "text", label: "Created At" },
            },
        },
        enquiry_details: {
            include: true,
            type: "nested",
            label: "Enquiry Details",
            fields: {
                id: { include: true, type: "text", label: "Enquiry ID" },
                current_status_code: { include: false, type: "text", label: "Current Status Code" },
                created_at: { include: false, type: "text", label: "Created At" },
                updated_at: { include: false, type: "text", label: "Updated At" },
                reseller_referral_code: { include: true, type: "text", label: "Reseller Referral Code" },
                // Only a partner-sourced lead has one, and the placeholder every
                // other field falls back to would read as "we are waiting for
                // this" on the many leads that will never have it.
                partner_lead_id: { include: true, type: "text", label: "Partner Lead ID", hideWhenBlank: true },
                lead_sourcing_date: { include: true, type: "text", label: "Lead Sourcing Date", hideWhenBlank: true },
                preferred_language: { include: true, type: "text", label: "Preferred Language" },
                application_number: { include: false, type: "text", label: "Application Number" },
                flow_id: { include: false, type: "text", label: "Flow ID" },
                flow_token: { include: false, type: "text", label: "Flow Token" },
                first_name: { include: true, type: "text", label: "First Name" },
                last_name: { include: true, type: "text", label: "Last Name" },
                gender: { include: true, type: "text", label: "Gender" },
                pan_number: { include: true, type: "text", label: "PAN Number" },
                date_of_birth: { include: true, type: "text", label: "Date of Birth" },
                current_address: { include: true, type: "text", label: "Current Address" },
                // Q13. Decides which branch section the lead carries, so it
                // belongs beside the rest of the basic details rather than
                // being inferred from which section happens to be present.
                income_type: { include: true, type: "text", label: "Customer Type" },
                // business_name is NOT here: Q22 asks it on the self-employed
                // branch, so it is declared under Business Details. Leaving it
                // in this section made it appear and disappear depending on
                // customer type.
                business_name: { include: false, type: "text", label: "Business Name" },
                business_pincode: { include: true, type: "text", label: "Business Pincode" },
                required_loan_amount: { include: true, type: "text", label: "Required Loan Amount" },
                requested_tenure_months: { include: true, type: "text", label: "Requested Tenure (Months)" },
                expected_roi: { include: true, type: "text", label: "Expected ROI", suffix: "%" },
                loan_purpose: { include: true, type: "text", label: "Loan Purpose" },
                application_submission_status: { include: true, type: "text", label: "Application Submission Status" },
                valuation_status: { include: true, type: "text", label: "Valuation Status" },
                whatsapp_number: { include: true, type: "text", label: "WhatsApp Number" },
                state: { include: true, type: "text", label: "State" },
                current_status: { include: false, type: "text", label: "Current Status" },
                // Email ID and Comfortable EMI are no longer asked. Comfortable
                // EMI was superseded by Requested Tenure and Expected ROI above.
            },
        },
        inprinciple_detail: {
            include: true,
            type: "nested",
            label: "In-Principle Detail",
            fields: {
                enquiry: { include: false, type: "text", label: "Enquiry" },
                request_timestamp: { include: false, type: "text", label: "Request Timestamp" },
                response_timestamp: { include: false, type: "text", label: "Response Timestamp" },
                api_decision: { include: true, type: "text", label: "In-Principle Decision" },
                sanction_loan_amount: { include: true, type: "text", label: "Sanction Loan Amount" },
                sanction_emi_amount: { include: true, type: "text", label: "Sanction EMI Amount" },
                sanction_tenure: { include: true, type: "text", label: "Sanction Tenure" },
                rejection_reasons: { include: true, type: "text", label: "Rejection Reasons" },
                message: { include: false, type: "text", label: "Message" },
                request_json: { include: false, type: "text", label: "Request JSON" },
                response_json: { include: false, type: "text", label: "Response JSON" },
                valuation: { include: false, type: "text", label: "Valuation" },
            },
        },
        employment_details: {
            include: true,
            type: "nested",
            label: "Employment Details",
            fields: {
                id: { include: false, type: "text", label: "ID" },
                employer_name: { include: true, type: "text", label: "Employer Name" },
                employment_profile: { include: true, type: "text", label: "Employment Profile" },
                current_job_tenure_months: { include: true, type: "text", label: "Current Job Tenure (Months)" },
                total_work_experience_months: { include: true, type: "text", label: "Total Work Experience (Months)" },
                salary_credit_mode: { include: true, type: "text", label: "Salary Credit Mode" },
                // net_monthly_income is shown under Income, where it falls back
                // to the pre-rework column.
                net_monthly_income: { include: false, type: "text", label: "Net Monthly Income" },
            },
        },
        business_details: {
            include: true,
            type: "nested",
            label: "Business Details",
            fields: {
                id: { include: false, type: "text", label: "ID" },
                business_name: {
                    include: true, type: "text", label: "Business Name",
                    source: "business_details.business_name",
                    fallback: "enquiry_details.business_name",
                },
                business_profile: { include: true, type: "text", label: "Business Profile" },
                business_type: { include: true, type: "text", label: "Business Type" },
                sub_industry: { include: true, type: "text", label: "Industry / Nature of Business" },
                business_vintage_years: { include: true, type: "text", label: "Business Vintage (Years)" },
                business_document_available: { include: true, type: "text", label: "Business Document Available" },
                // Revenue and profit are shown under Income, with fallbacks.
                monthly_revenue: { include: false, type: "text", label: "Monthly Revenue" },
                monthly_profit: { include: false, type: "text", label: "Monthly Profit" },
            },
        },
        income_assessment: {
            include: true,
            type: "nested",
            label: "Income",
            // Every figure here moved to a new table in the rework, so each one
            // reads the new location first and the IncomeAssessment column only
            // for enquiries that predate it.
            fields: {
                id: { include: false, type: "text", label: "ID" },
                enquiry: { include: false, type: "text", label: "Enquiry" },
                monthly_salary_income: {
                    include: true, type: "text", label: "Net Monthly Income",
                    source: "employment_details.net_monthly_income",
                    fallback: "income_assessment.monthly_salary_income",
                },
                monthly_total_revenue: {
                    include: true, type: "text", label: "Monthly Revenue",
                    source: "business_details.monthly_revenue",
                    fallback: "income_assessment.monthly_total_revenue",
                },
                monthly_net_profit: {
                    include: true, type: "text", label: "Monthly Profit",
                    source: "business_details.monthly_profit",
                    fallback: "income_assessment.monthly_net_profit",
                },
                // Rental income and EMI expenses were replaced by the Other
                // Income Sources form below. Household expenses is asked by no
                // question, before or after the rework.
                created_at: { include: false, type: "text", label: "Created At" },
                updated_at: { include: false, type: "text", label: "Updated At" },
            },
        },
        other_income_sources: {
            include: true,
            type: "list",
            label: "Other Income Sources",
            emptyLabel: "None declared",
            fields: {
                id: { include: false, type: "text", label: "ID" },
                source: { include: true, type: "text", label: "Source" },
                amount: { include: true, type: "text", label: "Amount" },
            },
        },
        balance_transfer_details: {
            include: true,
            type: "nested",
            label: "Balance Transfer Details",
            // Only the Balance Transfer product has this section. Without the
            // guard, a pre-rework LAP lead whose retired bt_loan_emi_amount
            // column holds 0 - which is most of them - renders a balance
            // transfer heading over a single meaningless figure, because 0 is
            // a real value and no emptiness check can exclude it.
            requires: { path: "loan_type.code", equals: "BT" },
            fields: {
                id: { include: false, type: "text", label: "ID" },
                existing_lender_name: { include: true, type: "text", label: "Existing Lender Name" },
                original_loan_amount: { include: true, type: "text", label: "Original Loan Amount" },
                principal_outstanding: { include: true, type: "text", label: "Principal Outstanding" },
                existing_roi: { include: true, type: "text", label: "Existing ROI", suffix: "%" },
                existing_emi: {
                    include: true, type: "text", label: "Existing EMI",
                    source: "balance_transfer_details.existing_emi",
                    fallback: "collateral_details.bt_loan_emi_amount",
                },
                disbursement_date: { include: true, type: "text", label: "Disbursement Date" },
                emis_paid: { include: true, type: "text", label: "EMIs Paid" },
                has_emi_bounce: { include: true, type: "text", label: "Any EMI Bounce / DPD" },
                max_dpd_days: { include: true, type: "text", label: "Maximum DPD (Days)" },
                topup_required: { include: true, type: "text", label: "Top-up Required" },
                topup_amount: { include: true, type: "text", label: "Top-up Amount" },
            },
        },
        collateral_details: {
            include: true,
            type: "nested",
            label: "Collateral Details",
            fields: {
                id: { include: false, type: "text", label: "ID" },
                property_type: { include: true, type: "text", label: "Property Type" },
                structure_type: { include: true, type: "text", label: "Property Sub-Type" },
                property_ownership: {
                    include: true, type: "text", label: "Property Ownership",
                    // Replaced the owner yes/no plus free-text owner name.
                    source: "collateral_details.property_ownership",
                    fallback: "collateral_details.property_owner_name",
                },
                property_address: {
                    include: true, type: "text", label: "Property Address",
                    source: "collateral_details.property_address",
                    fallback: "collateral_details.detailed_address",
                },
                occupancy_type: { include: true, type: "text", label: "Occupancy Type" },
                roof_type: { include: true, type: "text", label: "Roof Type" },
                land_area: { include: true, type: "text", label: "Total Area (Sq ft)" },
                built_up_area: { include: true, type: "text", label: "Built-Up Area (Sq ft)" },
                declared_valuation: { include: true, type: "text", label: "Estimated Market Value" },
                valuation_source: { include: true, type: "text", label: "Source of Property Value" },
                is_fully_constructed: { include: true, type: "text", label: "Is Property Fully Constructed?" },
                is_approved_regularised: { include: true, type: "text", label: "Is Property Approved / Regularised?" },
                // bt_loan_exists and bt_loan_emi_amount were replaced by the
                // Balance Transfer section; is_property_owner, property_owner_name
                // and detailed_address by the two fields above.
                created_at: { include: false, type: "text", label: "Created At" },
                updated_at: { include: false, type: "text", label: "Updated At" },
            },
        },
        collateral_location: {
            include: true,
            type: "nested",
            label: "Collateral Location",
            fields: {
                id: { include: false, type: "text", label: "ID" },
                state: { include: true, type: "text", label: "State" },
                zone: { include: true, type: "text", label: "Zone" },
                sub_registrar_office: { include: true, type: "text", label: "Sub-Registrar Office" },
                village: { include: true, type: "text", label: "Village" },
                street: { include: true, type: "text", label: "Street" },
                house_no: { include: true, type: "text", label: "House No." },
                street_building_colony_name: { include: true, type: "text", label: "Street / Building / Colony" },
                pincode: { include: true, type: "text", label: "Pincode" },
                registration_year: { include: true, type: "text", label: "Registration Year" },
                registered_document_number: { include: true, type: "text", label: "Registered Document Number" },
                sro: { include: true, type: "text", label: "SRO" },
                document_number_type: { include: true, type: "text", label: "Document Number Type" },
                document_type_tn: { include: true, type: "text", label: "Document Type (TN)" },
                survey_number_type: { include: true, type: "text", label: "Survey Number Type" },
                survey_number_verification_method: { include: true, type: "text", label: "Survey Number Verification Method" },
                survey_number: { include: true, type: "text", label: "Survey Number" },
                saledeed_link: { include: false, type: "text", label: "Sale Deed Link" },
                created_at: { include: false, type: "text", label: "Created At" },
                updated_at: { include: false, type: "text", label: "Updated At" },
            },
        }
    }
};

// Read a dotted path from the payload root. Returns undefined for any missing
// link in the chain, so a section the backend returned as null is simply
// absent rather than a crash.
export const getByPath = (root, path) =>
    path
        ? path.split(".").reduce(
            (value, key) => (value === null || value === undefined ? undefined : value[key]),
            root
        )
        : undefined;

const isBlank = (value) => value === null || value === undefined || value === "";

// A replaced field reads its new location first and the old one only when the
// new one holds nothing. Zero and false are real answers, so only null,
// undefined and empty string count as absent.
export const resolveFieldValue = (root, sectionData, key, fieldSchema) => {
    if (fieldSchema?.source) {
        const current = getByPath(root, fieldSchema.source);
        if (!isBlank(current)) return current;
        return getByPath(root, fieldSchema.fallback);
    }
    return sectionData ? sectionData[key] : undefined;
};

// True when a section would render at least one real value. Used to hide a
// section entirely rather than leave a bare heading over an empty box: after
// the rework most enquiries populate only one of the employment and business
// branches, and only a Balance Transfer lead has the BT section at all.
//
// It cannot be a plain emptiness check on the section's own data, because a
// field with a `source` reads from elsewhere in the payload - the Income
// section has no data of its own on a post-rework enquiry and must still show.
export const sectionMatchesRequirement = (root, schema) => {
    const rule = schema?.requires;
    if (!rule) return true;
    return getByPath(root, rule.path) === rule.equals;
};

export const sectionHasContent = (root, sectionData, schema) => {
    if (!schema?.include) return false;
    if (!sectionMatchesRequirement(root, schema)) return false;

    return Object.entries(schema.fields || {}).some(([key, fieldSchema]) => {
        if (!fieldSchema?.include) return false;

        if (fieldSchema.type === "nested") {
            const nested = sectionData?.[key];
            return sectionHasContent(
                root,
                nested && typeof nested === "object" ? nested : {},
                fieldSchema
            );
        }

        if (fieldSchema.type === "list") {
            return Array.isArray(sectionData?.[key]) && sectionData[key].length > 0;
        }

        if (!fieldSchema.source && !(sectionData && key in sectionData)) return false;

        return !isBlank(resolveFieldValue(root, sectionData, key, fieldSchema));
    });
};
